import { BrowserMultiFormatReader, BarcodeFormat, DecodeHintType, Result } from "@zxing/library";
import { SCAN_ERROR_MESSAGES, ORIENTATION, getDeviceOrientation } from "@/utils";
import { ScanModalError } from "@/components/modal/scan-modal/errors";

export const useCodeScannerAdapter = (onCodeFoundCallback: (code: string) => void, onCameraEnabledCallback: () => void) => {
  let codeReader: BrowserMultiFormatReader | null = null;

  const startCamera = async (scannerVideoRef: HTMLVideoElement, width: number, height: number) => {
    // Camera constraints are always based on landscape orientation.
    // For portrait we must cross values.
    const isPortrait = getDeviceOrientation() === ORIENTATION.portrait;
    const videoHeight = isPortrait ? width : height;
    const videoWidth = isPortrait ? height : width;

    /** Expected video constraints, the MediaDevices interface will try to match them as closely as possible
     * facingMode: "environment" defines the back camera as preferred (if there is a back camera).
     * height and width will depends on the camera capabilities.
     **/
    const videoConstraints = {
      video: {
        facingMode: "environment",
        height: { ideal: videoHeight },
        width: { ideal: videoWidth }
      }
    };

    try {
      await codeReader?.decodeFromConstraints(videoConstraints, scannerVideoRef, (result: Result) => {
        if (result) {
          const itemCode = result.getText();
          onCodeFoundCallback(itemCode);
        }
      });
    } catch (error) {
      throw new ScanModalError(SCAN_ERROR_MESSAGES.noCamera);
    }

    onCameraEnabledCallback();
  };

  const initScanner = async (scannerVideoRef: HTMLVideoElement, width: number, height: number) => {
    // This config defines the specific types of codes to scan.
    const hints = new Map();
    const formats = [
      //Most important formats
      BarcodeFormat.PDF_417,
      BarcodeFormat.QR_CODE,
      BarcodeFormat.UPC_A,
      BarcodeFormat.UPC_E,
      BarcodeFormat.UPC_EAN_EXTENSION,
      BarcodeFormat.DATA_MATRIX,
      // Other types:
      BarcodeFormat.AZTEC,
      BarcodeFormat.RSS_14,
      BarcodeFormat.UPC_EAN_EXTENSION,
      BarcodeFormat.CODE_39,
      BarcodeFormat.CODE_128,
      BarcodeFormat.EAN_8,
      BarcodeFormat.ITF
    ];

    hints.set(DecodeHintType.POSSIBLE_FORMATS, formats);

    try {
      codeReader = new BrowserMultiFormatReader(hints);
      const videoInputDevices = await codeReader.listVideoInputDevices();

      if (videoInputDevices.length) {
        await startCamera(scannerVideoRef, width, height);
        return;
      }

      throw new ScanModalError(SCAN_ERROR_MESSAGES.noCamera);
    } catch {
      throw new ScanModalError(SCAN_ERROR_MESSAGES.noCamera);
    }
  };

  const resetScanner = () => {
    codeReader?.reset();
  };

  return {
    initScanner,
    resetScanner
  };
};

export const listVideoInputDevices = async () => {
  try {
    const codeReader = new BrowserMultiFormatReader();
    const devices = await codeReader.listVideoInputDevices();
    return devices;
  } catch {
    // If an error occurs listing video input devices, there is no camera.
    return [];
  }
};
